import React, { useLayoutEffect, useRef, useState } from 'react';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../../messages/en-US';

/** Read-view clamp height — long notes collapse to this many lines behind a "Show more" toggle. */
const CLAMP_LINES = 3;

// Splits text on URLs (keeps the URLs as their own parts via the capturing group).
const URL_SPLIT = /(\bhttps?:\/\/[^\s]+|\bwww\.[^\s]+)/gi;
// Tests whether a single split part is a URL (non-global, so it has no lastIndex state).
const URL_TEST = /^(https?:\/\/|www\.)/i;
// Trailing punctuation that is almost certainly sentence punctuation, not part of the URL.
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

/**
 * Renders plain note text with any URL turned into a clickable link. Non-URL text is rendered as
 * React children (escaped — no HTML injection), so only http(s)/www substrings become anchors.
 * Bare `www.` links get an `https://` href; trailing sentence punctuation stays outside the link.
 */
function linkifyText(text: string): React.ReactNode {
  if (!text) return text;

  return text.split(URL_SPLIT).map((part, i) => {
    if (!part) return null;
    if (!URL_TEST.test(part)) return <React.Fragment key={i}>{part}</React.Fragment>;

    const trailing = part.match(TRAILING_PUNCTUATION)?.[0] ?? '';
    const url = trailing ? part.slice(0, part.length - trailing.length) : part;
    const href = /^www\./i.test(url) ? `https://${url}` : url;

    return (
      <React.Fragment key={i}>
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline break-all">
          {url}
        </a>
        {trailing}
      </React.Fragment>
    );
  });
}

/**
 * Note body in read mode: linkifies URLs and clamps long text to CLAMP_LINES with a
 * "Show more / Show less" toggle. Keeps the list scannable and avoids nested scrollbars
 * (one long note can't dominate the timeline).
 */
export default function NoteText({ text }: { text: string }) {
  const localizationService = useLocalization();
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // Detect overflow against the clamped box. Skip while expanded (no clamp → heights match)
  // so the "Show less" toggle stays visible. Re-measure on resize since wrapping is width-dependent.
  useLayoutEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (!el || expanded) return;
      setOverflowing(el.scrollHeight - el.clientHeight > 1);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [text, expanded]);

  const clampStyle: React.CSSProperties = expanded
    ? {}
    : {
        display: '-webkit-box',
        WebkitLineClamp: CLAMP_LINES,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      };

  const showMore = localizationService.toLanguageString('custom.noteShowMore', enMessages.custom.noteShowMore);
  const showLess = localizationService.toLanguageString('custom.noteShowLess', enMessages.custom.noteShowLess);

  return (
    <div>
      <div ref={ref} className="whitespace-pre-wrap break-words text-sm" style={clampStyle}>
        {linkifyText(text)}
      </div>
      {(overflowing || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs font-medium text-blue-600 hover:underline"
        >
          {expanded ? showLess : showMore}
        </button>
      )}
    </div>
  );
}
